// Pre-submit review (ticket 86d385wc0, Phase 2).
//
// The final recognition checkpoint before the irreversible NetSuite POST: a
// read-only summary of exactly what will be sent. Confirming here runs the real
// submit; this dialog builds nothing and mutates nothing.

import { useMemo } from 'react';
import { Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  AttentionLinesPanel,
  ProceedAckFooter,
  classifyPostingLines,
  postingLineLabel,
} from '@shared/posting';
import type { AddressDto, FirmOfferDetailDto, SalesOrderDto } from '../../types/salesOrder.types';
import { useSalesOrderStore } from '../../store/salesOrderStore';
import {
  AddressRow,
  ChecklistRow,
  Grid,
  Row,
  Section,
  type ResolvedAddr,
} from './reviewFields';
import { hasResolvablePrice, type LineItemsReadiness } from './lineItemsStatus';
import { aggregateRebarPricing } from '../../utils/pricing';
import {
  combinedOrderTotal,
  computeSoFreight,
  freightBillingNeedsPrompt,
  resolveIncludeFreightLine,
} from '../../utils/freight';
import { PerGradeBalanceChips } from '../PerGradeBalanceChips';
import { SourceKgNotice } from '../SourceKgNotice';
import { hasKilogramSourceLines } from '../../utils/lineWeight';

const PHP_FMT = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtPhp = (n: number) => PHP_FMT.format(n);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: SalesOrderDto;
  firmOffer: FirmOfferDetailDto | null;
  lineItems: LineItemsReadiness;
  onConfirm: () => void;
  isSubmitting: boolean;
}

function waivedFreightLabel(id: string | null | undefined): string {
  if (id === '1') return 'Yes';
  if (id === '2') return 'No';
  return '—';
}

// Saved pick → resolve label + full address text from the loaded address book (the draft
// stores only the id pre-submit). Custom typed address → the draft's denormalized fields.
function resolveAddr(
  addressId: string | null,
  addresseeName: string | null,
  lines: Array<string | null>,
  addresses: AddressDto[],
): ResolvedAddr {
  if (addressId) {
    const found = addresses.find((a) => a.internalId === addressId);
    if (!found) return null;
    const text = (found.addrText?.trim() || found.addressee?.trim() || '').replace(
      /\s*\n\s*/g,
      ', ',
    );
    return { label: found.label?.trim() || found.addressee?.trim() || null, text };
  }
  const text = [addresseeName, ...lines]
    .filter((p): p is string => !!p && p.trim().length > 0)
    .join(', ');
  return text ? { label: null, text } : null;
}

export function PreSubmitReviewDialog({
  open,
  onOpenChange,
  draft,
  firmOffer,
  lineItems,
  onConfirm,
  isSubmitting,
}: Props) {
  // Order totals — same shared utils as the Pricing panel + Line Items footer block, so the
  // final confirmation shows the exact figures the coordinator saw (ticket 86d39w791 Part B).
  const summary = aggregateRebarPricing(draft.lineItems, firmOffer);
  const freight = computeSoFreight({
    totalWeightKg: summary.totalWeightKg,
    // 86d3fteqa: editable Freight/Ton drives the charge; defaults from the FO rate.
    freightPerTon: draft.freightChargePerTon ?? firmOffer?.freightChargePerTon ?? null,
    waived: draft.waivedFreight === '1',
  });
  // Freight-line disposition (86d3addwy): omitted → 0 freight in the total + an explicit
  // "no freight line item" callout, so omission is unmistakable at the final gate.
  const freightLineIncluded = resolveIncludeFreightLine(
    draft.separateBilling,
    draft.includeFreightLine,
  );
  const effectiveFreightCharge = freightLineIncluded ? freight.charge : 0;
  const combined = combinedOrderTotal(summary.grandTotal, effectiveFreightCharge);
  const hasLines = summary.lineCount > 0;
  const sourceListedKg = hasKilogramSourceLines(draft.lineItems);

  // 86d3hm2vr: classify lines for the confirmation — blocking (unpostable) disables the
  // confirm; attention (spec advisories) requires the ack checkbox. Enforced PER LINE here,
  // not via the coarse readiness `blocker` (which passes a ready+unmatched mix through).
  const classified = useMemo(
    () =>
      classifyPostingLines(
        draft.lineItems.map((li) => ({
          key: li.clientId,
          label: postingLineLabel(li),
          diameterMm: li.diameterMm,
          gradeCode: li.gradeCode,
          isWeldable: li.isWeldable,
          matchedItemId: li.matchedItemId,
          quantity: li.quantity,
          hasResolvablePrice: hasResolvablePrice(li),
        })),
      ),
    [draft.lineItems],
  );

  // Customer = the user-picked deliver/bill-to (the entity the SO is actually booked to,
  // 86d3c80fr), not the FO customer. Addresses resolve label + full text from the loaded
  // address book (saved picks store only an id on the draft pre-submit).
  const addresses = useSalesOrderStore((s) => s.customerAddresses);
  const customer = draft.doCustCompanyName || firmOffer?.customer.refName || draft.customerName;
  const overallPrice =
    firmOffer?.pricing.overallPricePerKg != null
      ? `₱${firmOffer.pricing.overallPricePerKg.toFixed(2)}`
      : null;
  const shipping = resolveAddr(
    draft.shippingAddressId,
    draft.shippingAddresseeName,
    [draft.shippingAddrLine1, draft.shippingAddrLine2, draft.shippingAddrLine3],
    addresses,
  );
  const billing = resolveAddr(
    draft.billingAddressId,
    draft.billingAddresseeName,
    [draft.billingAddrLine1, draft.billingAddrLine2, draft.billingAddrLine3],
    addresses,
  );
  const poDisplay = draft.customerPoNumber
    ? draft.waitingForOfficialPo
      ? `${draft.customerPoNumber} · awaiting official PO`
      : draft.customerPoNumber
    : draft.waitingForOfficialPo
      ? 'Awaiting official PO'
      : null;
  // Delivery requirement checkboxes — always shown with their on/off state, mirroring the
  // Order Details "Delivery Requirements" group. Enabled order flags (Flags & Notes) ride
  // along as additional checked items.
  const deliveryReqs: Array<{ label: string; on: boolean }> = [
    { label: 'Trailer', on: draft.isTrailer },
    { label: 'M-F', on: draft.isMf },
    { label: 'PPE', on: draft.isPpeRequired },
    { label: 'Permit', on: draft.isPermitRequired },
  ];
  const flagItems: Array<{ label: string; on: boolean }> = [
    { label: 'Priority', on: draft.isPriority },
    { label: 'Spot', on: draft.isSpotOrder },
    { label: 'Reprint', on: draft.isReprint },
  ];

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review before creating the Sales Order</DialogTitle>
          <DialogDescription>
            This creates a Sales Order in NetSuite and can't be undone. Confirm the details below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Section title="Customer & Contract">
            <Grid>
              <Row label="Customer" value={customer} className="col-span-2" />
              <Row label="Firm Offer" value={firmOffer?.tranId ?? draft.linkedFirmOfferTranId} mono />
              <Row label="Terms" value={draft.termsName} />
              <Row
                label="Project"
                value={firmOffer?.projectName ?? draft.projectName}
                className="col-span-2"
              />
              <Row label="Customer PO #" value={poDisplay} />
              <Row label="PO expiry" value={draft.poExpiryDate} />
              <Row
                label="Contract expiry"
                value={draft.contractExpiryDate ?? firmOffer?.contractExpiryDate}
              />
              <Row label="Overall price / kg" value={overallPrice} mono />
            </Grid>
          </Section>

          <Section title="Shipping & Delivery">
            <Grid>
              <AddressRow label="Shipping" addr={shipping} />
              <AddressRow label="Billing" addr={billing} fallback="Defaults to customer" />
              <Row label="Delivery due" value={draft.deliveryDueDate} />
              <Row label="Waived freight" value={waivedFreightLabel(draft.waivedFreight)} />
              <Row label="Office contact" value={draft.officeContact1} />
              <Row label="Contact number" value={draft.contactNumber1} />
              {freightBillingNeedsPrompt(draft.separateBilling) && (
                <Row
                  label="Freight line"
                  value={
                    freightLineIncluded ? 'Included on this SO' : 'Omitted — no freight line item'
                  }
                  className="col-span-2"
                />
              )}
              {draft.authorizedReceivers && (
                <Row
                  label="Authorized receivers"
                  value={draft.authorizedReceivers}
                  className="col-span-2"
                />
              )}
              <ChecklistRow label="Delivery requirements" items={deliveryReqs} />
              <ChecklistRow label="Flags" items={flagItems} />
              {draft.otherInstructions && (
                <Row label="Instructions" value={draft.otherInstructions} className="col-span-2" />
              )}
              {draft.soRemarks && (
                <Row label="Other remarks" value={draft.soRemarks} className="col-span-2" />
              )}
            </Grid>
          </Section>

          <Section title="Line items">
            <p className="text-sm">
              <span className="font-semibold tabular-nums">{lineItems.ready}</span> ready
              {lineItems.needsAttention > 0 && (
                <span className="text-warning"> · {lineItems.needsAttention} still need attention</span>
              )}
            </p>
            {hasLines && firmOffer && (
              <PerGradeBalanceChips firmOffer={firmOffer} lineItems={draft.lineItems} />
            )}
            {/* 14ykpqtv3qw — last chance to check a kg-labelled source before the post. */}
            {hasLines && sourceListedKg && <SourceKgNotice />}
            <AttentionLinesPanel lines={classified} className="mt-1" />
          </Section>

          {hasLines && (
            <Section title="Estimated order total">
              <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
                <TotalRow label="Rebar grand total" value={fmtPhp(summary.grandTotal)} />
                <TotalRow
                  label={
                    !freightLineIncluded
                      ? 'Freight (omitted)'
                      : freight.waived
                        ? 'Freight (waived)'
                        : 'Freight (VAT-incl.)'
                  }
                  value={
                    !freightLineIncluded
                      ? fmtPhp(0)
                      : freight.waived
                        ? fmtPhp(0)
                        : freight.charge != null
                          ? fmtPhp(freight.charge)
                          : '—'
                  }
                />
                <TotalRow label="Order total" value={fmtPhp(combined)} strong />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Estimate — NetSuite computes the final on submit.
              </p>
            </Section>
          )}
        </div>

        <ProceedAckFooter
          blockingCount={classified.blocking.length}
          attentionCount={classified.attention.length}
          isSubmitting={isSubmitting}
          onCancel={() => onOpenChange(false)}
          onConfirm={onConfirm}
          confirmLabel="Create Sales Order"
          confirmIcon={<Send className="mr-1.5 h-4 w-4" />}
        />
      </DialogContent>
    </Dialog>
  );
}

function TotalRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-2 py-0.5',
        strong && 'mt-0.5 border-t pt-1.5',
      )}
    >
      <span className={cn('text-muted-foreground', strong && 'font-medium text-foreground')}>
        {label}
      </span>
      <span className={cn('font-mono tabular-nums', strong && 'font-semibold')}>{value}</span>
    </div>
  );
}

// Presentational field helpers (Section, Grid, Row, CheckItem, ChecklistRow, AddressRow)
// + ResolvedAddr now live in ./reviewFields, shared with SalesOrderReadOnlySummary (86d3ch831).
